import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { COUNTRIES, HOME_COUNTRY, type Country } from '@/lib/countries';

/**
 * Picking a country by its flag.
 *
 * This was a native `<select>` reading "+977 NP", which is two pieces of
 * jargon where a picture would do. A native select cannot render an image in
 * an option, so getting flags means owning the dropdown — hence this file
 * rather than three more lines in Field.tsx.
 *
 * **Flag emoji were the obvious answer and are the wrong one here.** Windows
 * ships no flag glyphs, so Chrome and Edge draw the regional indicators as
 * the two letters instead — on the client's own machines that would have
 * produced exactly the "NP" they asked us to get rid of. Images render
 * everywhere, so images it is.
 *
 * They come from flagcdn.com, lazily, and every one of them is allowed to
 * fail: the dial code is always on the button in text, and a flag that does
 * not load falls back to its ISO letters. A counter on bad hotel wifi still
 * gets a working control, just a plainer one.
 */

/** 24×18 is the smallest size that still reads as a flag rather than a smudge. */
function flagUrl(iso: string, retina = false): string {
  const size = retina ? '48x36' : '24x18';
  return `https://flagcdn.com/${size}/${iso.toLowerCase()}.png`;
}

function Flag({ country }: { country: Country }): ReactElement {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <span className="w-6 shrink-0 text-center text-micro font-semibold text-ink-muted">
        {country.iso}
      </span>
    );
  }
  return (
    <img
      src={flagUrl(country.iso)}
      srcSet={`${flagUrl(country.iso, true)} 2x`}
      width={24}
      height={18}
      loading="lazy"
      alt=""
      onError={() => setBroken(true)}
      className="w-6 shrink-0 rounded-[2px] border border-rule object-cover"
    />
  );
}

interface Props {
  readonly value: string;
  readonly onChange: (iso: string) => void;
}

/** Matches `w-72` below. The popup has to be measured before it is drawn. */
const POPUP_WIDTH = 288;
const POPUP_MAX_HEIGHT = 320;
const EDGE_GAP = 8;

export function CountrySelect({ value, onChange }: Props): ReactElement {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);

  const selected =
    COUNTRIES.find((country) => country.iso === value) ??
    COUNTRIES.find((country) => country.iso === HOME_COUNTRY)!;

  /**
   * Where the list goes, in viewport coordinates.
   *
   * It is positioned `fixed` rather than `absolute`, and that is the whole fix
   * for the enquiry modal. A modal body scrolls — `overflow-y-auto` — and an
   * absolutely positioned child of a scroll container is clipped by it, so the
   * list came out cut off and shoved the panel about. The booking form is a
   * plain page with nothing to clip against, which is why the same component
   * behaved there and not here.
   *
   * A fixed element is positioned against the viewport and is not clipped by
   * an ancestor's overflow, so it escapes without needing a portal — which
   * matters, because the modal is a real `<dialog>` in the top layer and
   * anything portalled to `document.body` would render behind it.
   */
  const place = (): void => {
    const anchor = button.current?.getBoundingClientRect();
    if (anchor === undefined) return;

    const room = window.innerHeight - anchor.bottom;
    const above = room < POPUP_MAX_HEIGHT && anchor.top > room;
    setAt({
      top: above ? anchor.top - POPUP_MAX_HEIGHT - 4 : anchor.bottom + 4,
      // Kept on screen: the phone box sits in the right-hand column of a
      // two-column row, and a 288px list hanging off it would otherwise run
      // past the edge of a laptop screen.
      left: Math.max(
        EDGE_GAP,
        Math.min(anchor.left, window.innerWidth - POPUP_WIDTH - EDGE_GAP),
      ),
    });
  };

  // Closed by a click anywhere else on the page. `mousedown` rather than
  // `click`, so the list is gone before whatever was clicked reacts.
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent): void => {
      if (!(event.target instanceof Node)) return;
      if (box.current !== null && !box.current.contains(event.target)) setOpen(false);
    };
    // `capture`, because the scroll that moves this is the modal body's, not
    // the window's, and a scroll event on an inner element does not bubble.
    const follow = (): void => place();
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  // `preventScroll`, and this is the second half of the glitch. The search box
  // had `autoFocus`, and focusing something inside a scroll container makes
  // the browser scroll it into view — so opening the list yanked the modal's
  // content up before the list had even been drawn.
  useEffect(() => {
    if (open) search.current?.focus({ preventScroll: true });
  }, [open]);

  // Nepal first, because it is the answer nineteen times in twenty here and
  // nobody should scroll past Namibia to reach it.
  const ordered = [
    ...COUNTRIES.filter((country) => country.iso === HOME_COUNTRY),
    ...COUNTRIES.filter((country) => country.iso !== HOME_COUNTRY),
  ];
  const needle = filter.trim().toLowerCase();
  const shown =
    needle === ''
      ? ordered
      : ordered.filter(
          (country) =>
            country.name.toLowerCase().includes(needle) ||
            country.iso.toLowerCase().startsWith(needle) ||
            country.dial.startsWith(needle.replace('+', '')),
        );

  function pick(iso: string): void {
    onChange(iso);
    setOpen(false);
    setFilter('');
  }

  return (
    <div ref={box} className="relative">
      <button
        ref={button}
        type="button"
        aria-label={`Country code: ${selected.name}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          // Measured before it opens, so the first paint is already in the
          // right place rather than jumping there.
          if (!open) place();
          setOpen((was) => !was);
        }}
        className="flex min-h-touch w-full items-center gap-1.5 rounded-sm border border-rule-strong bg-panel px-2 text-body text-ink"
      >
        <Flag country={selected} />
        <span className="tnum">+{selected.dial}</span>
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
          className="ml-auto shrink-0 text-ink-muted"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && at !== null && (
        <div
          style={{ top: at.top, left: at.left, width: POPUP_WIDTH }}
          className="fixed z-50 rounded-sm border border-rule-strong bg-panel shadow-lg"
        >
          <div className="border-b border-rule p-2">
            <input
              ref={search}
              type="text"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setOpen(false);
                // Enter takes the top match, which is what somebody who has
                // just typed "ind" is expecting to happen.
                if (event.key === 'Enter') {
                  event.preventDefault();
                  const first = shown[0];
                  if (first !== undefined) pick(first.iso);
                }
              }}
              placeholder="Search"
              className="min-h-touch w-full rounded-sm border border-rule bg-panel px-2.5 text-body"
            />
          </div>

          <ul role="listbox" aria-label="Country" className="max-h-64 overflow-y-auto overscroll-contain">
            {shown.map((country) => (
              <li key={country.iso} role="option" aria-selected={country.iso === selected.iso}>
                <button
                  type="button"
                  onClick={() => pick(country.iso)}
                  className={[
                    'flex w-full items-center gap-2 px-3 py-2 text-left',
                    country.iso === selected.iso ? 'bg-signal-wash font-medium' : 'hover:bg-paper',
                  ].join(' ')}
                >
                  <Flag country={country} />
                  <span className="min-w-0 flex-1 truncate">{country.name}</span>
                  <span className="tnum shrink-0 text-small text-ink-muted">+{country.dial}</span>
                </button>
              </li>
            ))}

            {shown.length === 0 && (
              <li className="px-3 py-3 text-small text-ink-muted">Nothing matches that.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
